import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/SimpleBadge';
import { Loading } from '../components/ui/SimpleLoading';
import { Alert } from '../components/ui/SimpleAlert';
import { apiService, type AnalyticsData } from '../services/api.service';

function defaultDateRange(period: string) {
  const now = new Date();
  const end = now.toISOString().slice(0, 10);
  let start: Date;

  switch (period) {
    case 'week': {
      start = new Date(now);
      start.setDate(start.getDate() - 6);
      break;
    }
    case 'quarter': {
      const qMonth = Math.floor(now.getMonth() / 3) * 3;
      start = new Date(now.getFullYear(), qMonth, 1);
      break;
    }
    case 'year':
      start = new Date(now.getFullYear(), 0, 1);
      break;
    case 'month':
    default:
      start = new Date(now.getFullYear(), now.getMonth(), 1);
  }

  return { startDate: start.toISOString().slice(0, 10), endDate: end };
}

export function Analytics() {
  const initialRange = defaultDateRange('month');
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [dateRange, setDateRange] = useState(initialRange);

  useEffect(() => {
    setDateRange(defaultDateRange(selectedPeriod));
  }, [selectedPeriod]);

  useEffect(() => {
    loadAnalytics();
  }, [dateRange.startDate, dateRange.endDate]);

  const loadAnalytics = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await apiService.getAnalytics({
        from: dateRange.startDate,
        to: dateRange.endDate,
      });
      setAnalyticsData(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar analytics');
    } finally {
      setIsLoading(false);
    }
  };

  const maxLoans = useMemo(
    () => Math.max(1, ...(analyticsData?.monthlyTrend.map((m) => m.loans) ?? [1])),
    [analyticsData],
  );

  const getPerformanceBadge = (value: number, type: 'rate' | 'amount') => {
    if (type === 'rate') {
      if (value >= 80) return 'success';
      if (value >= 60) return 'warning';
      return 'danger';
    }
    if (value >= 100000) return 'success';
    if (value >= 50000) return 'warning';
    return 'danger';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loading size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Alert variant="error" title="Error">
          {error}
        </Alert>
      </div>
    );
  }

  if (!analyticsData) {
    return (
      <div className="p-6">
        <Alert variant="warning" title="Sin datos">
          No se pudieron cargar los datos de analytics
        </Alert>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Analytics</h1>
        <div className="flex space-x-3">
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="week">Esta Semana</option>
            <option value="month">Este Mes</option>
            <option value="quarter">Este Trimestre</option>
            <option value="year">Este Año</option>
          </select>
          <Button onClick={loadAnalytics}>Actualizar</Button>
        </div>
      </div>

      <Card className="mb-6">
        <div className="flex space-x-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Fecha Inicio</label>
            <Input
              type="date"
              value={dateRange.startDate}
              onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Fecha Fin</label>
            <Input
              type="date"
              value={dateRange.endDate}
              onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
            />
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card>
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                <span className="text-blue-600 text-lg">💰</span>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Préstamos (período)</p>
              <p className="text-2xl font-semibold text-gray-900">{analyticsData.totalLoans}</p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                <span className="text-green-600 text-lg">📈</span>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Monto Desembolsado</p>
              <p className="text-2xl font-semibold text-gray-900">
                ${analyticsData.totalAmount.toLocaleString('es-AR')}
              </p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center">
                <span className="text-yellow-600 text-lg">✅</span>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Tasa de Aprobación</p>
              <div className="flex items-center">
                <p className="text-2xl font-semibold text-gray-900">{analyticsData.approvalRate}%</p>
                <Badge variant={getPerformanceBadge(analyticsData.approvalRate, 'rate')} className="ml-2">
                  {analyticsData.approvalRate >= 80 ? 'Excelente' : analyticsData.approvalRate >= 60 ? 'Bueno' : 'Regular'}
                </Badge>
              </div>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                <span className="text-red-600 text-lg">⚠️</span>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Cuotas en Mora</p>
              <div className="flex items-center">
                <p className="text-2xl font-semibold text-gray-900">{analyticsData.defaultRate}%</p>
                <Badge variant={analyticsData.defaultRate <= 5 ? 'success' : 'danger'} className="ml-2">
                  {analyticsData.defaultRate <= 5 ? 'Bajo' : 'Alto'}
                </Badge>
              </div>
            </div>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <Card>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Rendimiento Financiero</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-500">Ingresos (intereses + mora)</span>
              <span className="text-sm font-medium text-green-600">
                ${analyticsData.revenue.toLocaleString('es-AR')}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-500">Cobrado en período</span>
              <span className="text-sm font-medium text-blue-600">
                ${analyticsData.totalCollected.toLocaleString('es-AR')}
              </span>
            </div>
            <div className="flex justify-between items-center border-t pt-2">
              <span className="text-sm font-medium text-gray-900">Pagos registrados</span>
              <span className="text-sm font-bold text-gray-900">{analyticsData.totalPayments}</span>
            </div>
          </div>
        </Card>

        <Card>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Estadísticas de Préstamos</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-500">Préstamo Promedio</span>
              <span className="text-sm font-medium">
                ${analyticsData.averageLoanSize.toLocaleString('es-AR')}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-500">Clientes nuevos</span>
              <span className="text-sm font-medium">{analyticsData.totalCustomers}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-500">Cobranzas pendientes</span>
              <span className="text-sm font-medium">{analyticsData.pendingCollections}</span>
            </div>
          </div>
        </Card>

        <Card>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Más reportes</h3>
          <p className="text-sm text-gray-500 mb-4">
            Exportaciones CSV y reportes operativos (morosidad, ventas, financiado mensual).
          </p>
          <div className="flex flex-col gap-2">
            <Link to="/reports" className="text-sm text-blue-600 hover:underline">
              Centro de exportaciones →
            </Link>
            <Link to="/reports/operational" className="text-sm text-blue-600 hover:underline">
              Reportes operativos →
            </Link>
          </div>
        </Card>
      </div>

      <Card>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Tendencia mensual</h3>
        {analyticsData.monthlyTrend.length === 0 ? (
          <p className="text-sm text-gray-500">Sin datos en el período seleccionado.</p>
        ) : (
          <div className="space-y-3">
            {analyticsData.monthlyTrend.map((m) => (
              <div key={m.key} className="flex items-center gap-4">
                <span className="w-16 text-sm text-gray-600 shrink-0">{m.label}</span>
                <div className="flex-1 h-6 bg-gray-100 rounded overflow-hidden">
                  <div
                    className="h-full bg-blue-500 rounded"
                    style={{ width: `${Math.round((m.loans / maxLoans) * 100)}%` }}
                    title={`${m.loans} préstamos`}
                  />
                </div>
                <span className="w-20 text-right text-sm text-gray-700">{m.loans} prést.</span>
                <span className="w-28 text-right text-sm text-gray-500">
                  ${m.collected.toLocaleString('es-AR')}
                </span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
